import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import CompanyFeatureFlagsEnum, { CompanyFeatureFlagsTips } from 'ab-enums/companyFeatureFlags.enum';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import DateInput from 'af-components/Controls/DateInput';
import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import * as CompanyActions from 'af-actions/companies';

import PreviewItem from '../../Shared/PreviewItem';

const _TOMORROW = TimeUtils.addDays(new Date(), 1).toDate();
const CALENDAR_SETTINGS = { minDate: _TOMORROW };

type OwnProps = {
	showModal: boolean;
	closeModal: () => void;
	timeRoundingPolicyFrom: Nullable<string>;
};

type Props = OwnProps & ConnectedProps<typeof connector>;

const EnableCompanyNewTimeRoundingPolicy: React.FC<Props> = ({ closeModal, showModal, timeRoundingPolicyFrom, editTimeRoundingDate }) => {
	const [enableTimeRounding, setEnableTimeRounding] = React.useState(!!timeRoundingPolicyFrom);
	const [dateFrom, setDateFrom] = React.useState<Nullable<Date>>(
		timeRoundingPolicyFrom ? TimeUtils.parseDate(timeRoundingPolicyFrom, TimeFormat.DB_DATE_ONLY) : null
	);

	const renderLabel = React.useCallback(() => {
		return (
			<PreviewItem
				checked={!!timeRoundingPolicyFrom}
				hint={CompanyFeatureFlagsTips.startRoundingTimesTo1min}
				indent={false}
				label={CompanyFeatureFlagsEnum.startRoundingTimesTo1min}
				showCheckbox={false}
			/>
		);
	}, [timeRoundingPolicyFrom]);

	const handleChange = React.useCallback((event) => {
		setEnableTimeRounding(event.target.checked);
	}, []);

	const handleSubmit = React.useCallback(async () => {
		if (enableTimeRounding && dateFrom) {
			await editTimeRoundingDate({ date: TimeUtils.toDatabaseDateOnly(dateFrom) });
		} else {
			await editTimeRoundingDate({ date: null });
		}
		closeModal();
	}, [enableTimeRounding, dateFrom, closeModal, editTimeRoundingDate]);

	const handleDateChange = React.useCallback((newDate: Date) => setDateFrom(newDate), []);

	const isDisabled = React.useMemo(() => {
		if (timeRoundingPolicyFrom) {
			return new Date() >= TimeUtils.parseDate(timeRoundingPolicyFrom, TimeFormat.DB_DATE_ONLY);
		}
		return false;
	}, [timeRoundingPolicyFrom]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title="Edit Time Rounding"
			/>
			<CustomModal.Body>
				<Row>
					<Col sm={12}>
						<Checkbox
							handleChange={handleChange}
							isChecked={enableTimeRounding}
							isDisabled={isDisabled}
							label={renderLabel()}
						/>
					</Col>
					<Col sm={6}>
						{
							enableTimeRounding &&
							<DateInput
								calendarSettings={CALENDAR_SETTINGS}
								defaultValue={dateFrom ?? _TOMORROW}
								disabled={isDisabled}
								fixed={true}
								isStandalone={true}
								onValueChange={handleDateChange}
								selectedValue={dateFrom ?? undefined}
								showTodayButton={false}
							/>
						}
					</Col>
				</Row>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<SubmitButton
					disabled={isDisabled}
					label="Save"
					onClick={handleSubmit}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		editTimeRoundingDate: CompanyActions.editNewTimeRoundingPolicyDate,
	};
}

const connector = connect(null, mapDispatchToProps());

export default React.memo(connector(EnableCompanyNewTimeRoundingPolicy));
